<template>
  <div class="inventory-info">
    <div class="inventory-info_profile ovh">
      <div class="inventory-info_profile-avatar">
        <img
          :src="user?.avatar_url"
          alt=""
        >
      </div>
      <div class="inventory-info_profile__wrap fg ovh">
        <div class="inventory-info_profile-name f gap8">
          <p class="ell">
            {{ user?.persona_name }}
          </p>
          <nuxt-link
            v-if="user?.pro_player_nickname"
            :to="localePath(`/pro-players/${user?.pro_player_nickname}`)"
            class="btn-link-arrow primary fw700 f16 mb-4"
            target="_blank"
          >
            PRO
          </nuxt-link>
        </div>
        <div class="inventory-info_profile-socials">
          <a
            v-if="user?.steam64"
            :href="`https://steamcommunity.com/profiles/${user?.steam64}`"
            class="inventory-info_profile-socials_item"
            rel="noindex nofollow noopener"
            target="_blank"
          >
            <svg
              height="26"
              width="26"
            >
              <use xlink:href="/icons/sprite.svg#i-steam-circle-2" />
            </svg>
          </a>
          <template v-for="item in possibleSocials">
            <a
              v-if="user?.social_links?.[item.social]"
              :key="user?.social_links?.[item.social]"
              :href="user?.social_links?.[item.social]"
              class="inventory-info_profile-socials_item"
              rel="noindex nofollow noopener"
              target="_blank"
            >
              <svg
                height="26"
                width="26"
              >
                <use :xlink:href="`/icons/sprite.svg#${item.icon}`" />
              </svg>
            </a>
          </template>
        </div>
      </div>
    </div>
    <div class="inventory-info_stat">
      <div class="f fs mb4">
        <i
          v-if="!total"
          class="i-loading mt7 mb8"
        />
        <p v-else>
          <UiPrice
            :decimal="0"
            :price="total?.value"
          />
        </p>
        <div
          :class="{
            green: Number(user?.total_value_change_percentage) > 0,
            red: Number(user?.total_value_change_percentage) < 0,
          }"
          class="aup ml10 f fs"
        >
          {{ user?.total_value_change_percentage.toFixed(2) }}%
        </div>
      </div>
      <div v-if="total">
        {{ total.count }} {{ t("items").toLocaleLowerCase() }}
      </div>
      <div v-else>
        {{ t("loading_items") }}
      </div>
    </div>
    <div
      v-if="categoryStats.length"
      class="inventory-info_graph"
    >
      <div
        v-for="stat in categoryStats"
        :key="stat.name"
        class="inventory-info_graph-item"
      >
        <p>{{ stat.name }}</p>
        <div class="progressbar">
          <div
            :style="{ width: `${getLineWidth(stat.percentage)}%` }"
            class="progressbar_wrap"
          >
            <div class="progressbar_inner" />
            <div class="tooltip">
              <div class="tooltip-content">
                <div>{{ stat.count }} {{ stat.name }}</div>
                <div>
                  <UiPrice
                    :decimal="0"
                    :price="stat.totalPrice"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div
      v-if="['private', 'private_inventory'].includes(user?.steam_state || '')"
      class="inventory-info_alert"
    >
      <UiAlert
        :description="t('inventory.archived_desc')"
        :title="t('inventory.archived')"
        icon="archive"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { UserInventoryProfile } from '~/graphql/generated/schema-types';

const props = defineProps<{
  data: UserInventoryProfile | undefined
  catalog: any
}>();

const possibleSocials = [
  {
    social: 'facebook',
    icon: 'i-facebook',
  },
  {
    social: 'github',
    icon: 'i-github',
  },
  {
    social: 'instagram',
    icon: 'i-instagram-2',
  },
  {
    social: 'linkedin',
    icon: 'i-linkedin',
  },
  {
    social: 'reddit',
    icon: 'i-reddit',
  },
  {
    social: 'tiktok',
    icon: 'i-tiktok',
  },
  {
    social: 'twitter',
    icon: 'i-twitter',
  },
  {
    social: 'vk',
    icon: 'i-vk',
  },
  {
    social: 'youtube',
    icon: 'i-youtube',
  },
];

const localePath = useLocalePath();
const { t } = useI18n();
const user = computed(() => props.data);
const items = computed(() => props.catalog);
const total = computed(() =>
  props.catalog
    ? {
        count: props.catalog?.length,
        value: props.catalog?.reduce((acc: number, item: any) => {
          const itemPrice = item.price || 0;
          const stickersPrice
                = item.stickers?.reduce(
                  (sAcc: number, sticker: any) => sAcc + (sticker.price || 0) * 0.05,
                  0,
                ) || 0;
          return acc + itemPrice + stickersPrice;
        }, 0),
      }
    : null,
);

interface CategoryStat {
  name: string
  count: number
  totalPrice: number
  percentage: number
}

const mainCategories = [
  'Stickers',
  'Snipers',
  'Knives',
  'Gloves',
  'Rifles',
  'Pistols',
  'SMGs',
  'Heavy',
  'Shotguns',
];
const categoryStats = computed(() => {
  if (!items.value?.length)
    return [];

  const stats = items.value.reduce(
    (acc: Record<string, CategoryStat>, item: any) => {
      const category = !mainCategories.includes(item.category) ? 'Other' : item.category;

      const itemPrice = item.price || 0;
      const stickersPrice
            = item.stickers?.reduce((sAcc: number, sticker: any) => sAcc + (sticker.price || 0), 0) || 0;
      const totalItemPrice = itemPrice + stickersPrice;

      if (!acc[category]) {
        acc[category] = {
          name: category,
          count: 0,
          totalPrice: 0,
          percentage: 0,
        };
      }
      acc[category].count++;
      acc[category].totalPrice += totalItemPrice;
      return acc;
    },
    {} as Record<string, Omit<CategoryStat, 'percentage'>>,
  );

  const totalInventoryPrice = Object.values(stats).reduce((sum, stat) => sum + stat.totalPrice, 0);

  return Object.values(stats)
    .map(stat => ({
      ...stat,
      percentage: (stat.totalPrice / totalInventoryPrice) * 100,
    }))
    .sort((a, b) => b.percentage - a.percentage);
});

function getLineWidth(percent: number) {
  return (percent / (categoryStats.value[0]?.percentage || 0)) * 100;
}
</script>

<style lang="scss" scoped>
.inventory-info {
  background: var(--surfaces-low-container);
  display: grid;
  gap: 28px;
  padding: 28px;
  border-radius: 16px;

  &_profile {
    display: flex;
    gap: 20px;
    align-items: center;

    &-avatar {
      display: flex;

      img {
        width: 60px;
        min-width: 60px;
        height: 60px;
        object-fit: cover;
        border-radius: 60px;
      }
    }

    &-name {
      font-size: 18px;
      font-weight: 800;
      color: var(--surfaces-on-surface);
      margin: 6px 0;
    }

    &-socials {
      display: flex;
      margin-left: -2px;
      margin-bottom: -4px;
      gap: 4px;
    }
  }

  &_stat {
    position: relative;
    padding-left: 16px;

    &:before {
      content: "";
      display: block;
      width: 4px;
      height: 54px;
      background: var(--accent-primary);
      border-radius: 4px;
      position: absolute;
      left: 0;
    }

    p {
      font-size: 24px;
      font-weight: 600;
      color: var(--accent-on-accent-secondary);
    }

    .adown {
      font-weight: 500;

      &:after {
        content: "";
        display: flex;
        width: 0;
        height: 0;
        margin: 0.3rem;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-top: 4px solid currentColor;
      }
    }

    .aup {
      font-weight: 500;

      &:after {
        content: "";
        display: flex;
        width: 0;
        height: 0;
        margin: 0.3rem;
        border-left: 4px solid transparent;
        border-right: 4px solid transparent;
        border-bottom: 4px solid currentColor;
      }
    }
  }

  &_graph {
    display: flex;
    flex-direction: column;
    gap: 8px;

    &-item {
      display: grid;
      grid-template-columns: 1fr 4fr;

      p {
        margin-bottom: 4px;
        margin-right: 4px;
        color: var(--surfaces-on-surface-variable);
      }
    }
  }
}

.tooltip {
  position: absolute;
  left: 50%;
  bottom: calc(100% + 8px);
  transform: translateX(-50%) translateY(-6px);
  background: var(--accent-on-primary);
  padding: 8px 12px;
  border-radius: 4px;
  font-size: 14px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.2s ease;
  z-index: 1000;

  &-content {
    text-align: center;
    color: #fff;
  }

  &::after {
    content: "";
    position: absolute;
    bottom: -5px;
    left: 50%;
    transform: translateX(-50%);
    border-width: 6px 6px 0;
    border-style: solid;
    border-color: var(--accent-on-primary) transparent transparent;
  }
}

.progressbar {
  height: 24px;
  border-radius: 4px;
  position: relative;

  &_wrap {
    height: inherit;
    position: relative;
  }

  &_inner {
    height: inherit;
    position: relative;
    background: var(--accent-secondary);
    border-radius: 4px;
    transition: all 0.3s ease;
    min-width: 4px;

    &:hover {
      background: #58e2b0;

      & + .tooltip {
        opacity: 1;
        visibility: visible;
        transform: translateX(-50%) translateY(-2px);
      }
    }
  }
}
</style>
